import { c as _c } from "react/compiler-runtime";
import { chainSettings, type ChainType, fallbackChainId } from "@/constants/chain-specific";
import { BlockscoutApi, type TokenBalance, type AddressNFTCollection } from "@/lib/blockscout-api";
import { explorerUrl } from "@/util/explorerLinks";
import { filterBlacklistedTokens, filterNfts } from "@/util/portfolio/tokenFilters";
import { useQuery } from "wagmi/query";
interface UserBalances {
  tokens: TokenBalance[] | null;
  nfts: {
    erc721: TokenBalance[];
    erc1155: TokenBalance[];
  };
}

// Dogechain API response types
interface DogechainTokenResponse {
  contractAddress: string;
  decimals: string;
  name: string;
  symbol: string;
  type: string;
  balance: string;
}
interface DogechainApiResponse {
  status: string;
  message: string;
  result: DogechainTokenResponse[];
}

// Function to fetch NFT collections from Dogechain's older API
const fetchDogechainNFTCollections = async (address: string): Promise<TokenBalance[]> => {
  try {
    // Try to get NFT collections using the older API format
    // Dogechain's older API might have different endpoints for NFTs
    const nftEndpoint = `https://explorer.dogechain.dog/api?module=account&action=tokenlist&address=${address}`;
    const response = await fetch(nftEndpoint);
    if (!response.ok) {
      console.warn('Failed to fetch NFT collections from Dogechain API');
      return [];
    }
    const data: DogechainApiResponse = await response.json();
    if (data.status !== "1" || !data.result) {
      return [];
    }

    // Filter for NFT tokens (ERC-721 and ERC-1155)
    const nftTokens = data.result.filter(token => token.type === "ERC-721" || token.type === "ERC-1155").map(token => ({
      token: {
        address: token.contractAddress,
        decimals: token.decimals,
        name: token.name,
        symbol: token.symbol,
        type: token.type
      },
      token_id: token.contractAddress,
      value: token.balance,
      // Add placeholder metadata structure for compatibility
      token_instance: {
        metadata: {
          token_instances: [] // Will be populated by real-time balance checking
        }
      } as any
    }) as TokenBalance);
    return nftTokens;
  } catch (error) {
    console.error('Error fetching Dogechain NFT collections:', error);
    return [];
  }
};
const fetchTokenBalances = async (address: string, chainId: ChainType): Promise<UserBalances> => {
  try {
    let response: Response;
    if (chainId === 2000) {
      const endpoint = `https://explorer.dogechain.dog/api?module=account&action=tokenlist&address=${address}`;
      response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Failed to fetch token balances: ${response.statusText}`);
      }
      const data: DogechainApiResponse = await response.json();
      if (data.status !== "1" || !data.result) {
        return {
          tokens: [],
          nfts: {
            erc721: [],
            erc1155: []
          }
        };
      }
      const tokens = data.result.map((token: DogechainTokenResponse) => ({
        token: {
          address: token.contractAddress,
          decimals: token.decimals,
          name: token.name,
          symbol: token.symbol,
          type: token.type
        },
        token_id: token.contractAddress,
        value: token.balance
      }) as TokenBalance);
      const filteredTokens = filterBlacklistedTokens(tokens);
      const onlyTokens = filteredTokens.filter(token => token.token.type === "ERC-20");

      // Fetch NFT collections separately for Dogechain
      const nftCollections = await fetchDogechainNFTCollections(address);
      const filteredNftTokens = filterBlacklistedTokens(nftCollections);
      const onlyNfts = filterNfts(filteredNftTokens);
      return {
        tokens: onlyTokens,
        nfts: onlyNfts
      };
    }
    const api = new BlockscoutApi({
      baseUrl: `${explorerUrl(chainId ?? fallbackChainId)}/api/v2/`
    });

    // Fetch regular token balances (ERC-20 only)
    response = await api.addresses.getAddressTokenBalances(address);
    if (!response.ok) {
      throw new Error(`Failed to fetch token balances: ${response.statusText}`);
    }
    const data = await response.json();
    const filteredTokens = filterBlacklistedTokens(data);
    const onlyTokens = filteredTokens.filter(token => token.token.type === "ERC-20");

    // Fetch NFT collections separately using the proper endpoint
    let nftCollections: AddressNFTCollection[] = [];
    try {
      const nftResponse = await api.addresses.getAddressNftCollections(address);
      if (nftResponse.ok) {
        const nftData = await nftResponse.json();
        nftCollections = nftData.items || [];
        console.log('NFT Collections fetched:', nftCollections);
      }
    } catch (error) {
      console.warn("Failed to fetch NFT collections:", error);
    }

    // Convert NFT collections to TokenBalance format for compatibility
    const nftTokenBalances: TokenBalance[] = await Promise.all(nftCollections.map(async collection => {
      // Ensure we have a valid balance value, never 0 unless truly empty
      const balanceValue = collection.amount || collection.token_instances?.length?.toString() || "1";

      // API returns address_hash instead of address for NFT collections too
      let contractAddress = collection.token.address || (collection.token as any)?.address_hash;

      // If no contract address in token, try to get it from token_instances or other sources
      if (!contractAddress && collection.token_instances && collection.token_instances.length > 0) {
        // Look for contract address in the first token instance
        const firstInstance = collection.token_instances[0];
        // The contract address might be in a different field - let's check all possible locations
        contractAddress = (firstInstance.token && typeof firstInstance.token === "object" && "address" in firstInstance.token ? (firstInstance.token as any).address : undefined) || (firstInstance.token && typeof firstInstance.token === "object" && "address_hash" in firstInstance.token ? (firstInstance.token as any).address_hash : undefined) || (firstInstance as any).contract_address || (firstInstance as any).contractAddress || (firstInstance as any).contract_address_hash || (firstInstance as any).token_contract_address_hash;
      }

      // Last resort: try to find the collection by name in our constants
      if (!contractAddress && collection.token.name) {
        try {
          // Dynamic import to avoid circular dependencies
          const collectionsModule = await import('@/constants/marketplace/collections');
          const knownCollection = collectionsModule.getCollectionByName?.(collection.token.name, chainId);
          if (knownCollection?.address) {
            contractAddress = knownCollection.address;
            console.log(`Found contract address via collection name lookup: ${contractAddress}`);
          }
        } catch (error) {
          // Ignore if the function doesn't exist or import fails
          console.warn('Could not lookup collection by name:', error);
        }
      }
      console.log('Converting NFT collection:', {
        collectionName: collection.token.name,
        originalAddress: collection.token.address,
        addressHash: (collection.token as any)?.address_hash,
        finalAddress: contractAddress,
        balanceValue,
        fullCollection: collection,
        // Log the full collection to see structure
        firstInstance: collection.token_instances?.[0] // Log first instance to see structure
      });
      return {
        token: {
          address: contractAddress,
          // Also store address_hash for compatibility
          address_hash: (collection.token as any)?.address_hash,
          name: collection.token.name,
          symbol: collection.token.symbol,
          type: collection.token.type,
          decimals: "0" // NFTs don't have decimals
        },
        value: balanceValue,
        token_id: contractAddress,
        // Use contract address as identifier
        // Store token instances in metadata for later access
        token_instance: {
          metadata: {
            token_instances: collection.token_instances
          }
        } as any
      };
    }));
    const filteredNftTokens = filterBlacklistedTokens(nftTokenBalances);
    const onlyNfts = filterNfts(filteredNftTokens);
    return {
      tokens: onlyTokens,
      nfts: onlyNfts
    };
  } catch (error) {
    console.error("Error fetching token balances:", error);
    return {
      tokens: null,
      nfts: {
        erc721: [],
        erc1155: []
      }
    };
  }
};
export const useAddressTokenBalances = (address, chainId) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t0 = ["addressTokenBalances", address, chainId];
    t1 = () => fetchTokenBalances(address, chainId);
    $[0] = address;
    $[1] = chainId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = !!address && !!chainId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 300000,
      gcTime: 600000,
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};